import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { Authorization } from '../../model/Authorization';
import { CustomerService } from '../../services/customer/customer.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AuthorizationService, CaptureData } from 'src/app/services/authorization/authorization.service';

/** Dialog data provided to Capture Authorization Dialog */
export interface CaptureAuthorizationDialogData {
  authorization: Authorization;
}

@Component({
    selector: 'app-capture-authorization-dialog',
    templateUrl: './capture-authorization-dialog.component.html',
    styleUrl: './capture-authorization-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CaptureAuthorizationDialogComponent implements OnInit {
  /** Dependency injections */
  private readonly _dialogRef: MatDialogRef<CaptureAuthorizationDialogComponent> = inject(MatDialogRef);
  private readonly _customerService: CustomerService = inject(CustomerService);
  private readonly _authorizationService: AuthorizationService = inject(AuthorizationService);
  private readonly _toastrService: ToastrService = inject(ToastrService);

  /** Dialog data */
  public data: CaptureAuthorizationDialogData = inject(MAT_DIALOG_DATA);

  /** Dynamic form variables */
  captureForm: FormGroup;
  isSubmitting = false;

  /** Loading flags */
  isLoadingCreditPlans: boolean = true;

  /**
   * On construct, initializes the capture form
   */
  constructor() {
    this.captureForm = new FormGroup({
      amount: new FormControl('', [
        Validators.required,
        Validators.min(0.01),
        Validators.max(this.data.authorization.remainingAmount),
        Validators.pattern(/^\d+(\.\d{1,2})?$/) // Allows numbers with up to 2 decimals
      ]),
      memo: new FormControl('')
    });
  }

  /**
   * On initialization, sets up reactive form value changes
   */
  ngOnInit(): void {
    // Reset credit plans
    this.captureForm.get('payment')?.setValue(this.data.authorization.remainingAmount);
  }

  /**
   * Handle capture form submission
   */
  public submit(): void {
    // Check if the form is valid
    if (this.captureForm.invalid) {
      this._toastrService.error('Please fill in all required fields correctly');
      return;
    }

    this.isSubmitting = true;

    try {
      // Create capture data to send
      const formValues = this.captureForm.value;
      const captureData: CaptureData = {
        amount: Number(formValues.amount),
        complete: Number(formValues.amount) === this.data.authorization.remainingAmount,
        planId: formValues.payment,
        memo: formValues.memo || '',
      };

      // Submit the capture request
      this._authorizationService.captureAuthorization(this.data.authorization.id, captureData)
      .pipe(finalize(() => setTimeout(() => this.isSubmitting = false, 1000)))
      .subscribe({
        next: () => {
          this._toastrService.success('Capture sent successfully');
          this.close(true);
        },
        error: (err: any) => {
          console.log('[CAPTURE-AUTHORIZATION-DIALOG-COMPONENT]: could not capture authorization', err);
          this._toastrService.error('Failed to send capture');
        }
      });
    } catch (err: any) {
      // Catch errors from form validation or transaction service
      console.error('[CAPTURE-AUTHORIZATION-DIALOG-COMPONENT]: Error capturing authorization:', err);
      this._toastrService.error('Failed to send capture');
      this.isSubmitting = false;
    }
  }

  /**
   * Close the dialog
   */
  public close(success: boolean = false) {
    this._dialogRef.close({ success });
  }
}
